import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Crown, X, Sparkles, Zap } from "lucide-react";
import { useApp } from "../context";
import { FOUNDER_UPSELL } from "../utils/founderPlans";
import { isPromptFree } from "../lib/promptFree";

/**
 * Tier-aware upgrade nudge that appears as a slide-in toast in the bottom-right
 * corner when a FREE user (a) triggers exit-intent (mouse toward top of viewport)
 * or (b) scrolls 60% down any page containing premium content.
 *
 * Frequency caps:
 *   - Max ONE nudge per browser session (sessionStorage)
 *   - If dismissed, remembered for 24h (localStorage)
 *   - Never shown on /subscribe, /payment/*, /login, /signup pages
 *   - Never shown for premium users (access_tier >= 2)
 */
const DISMISS_KEY = "iw_upgrade_nudge_dismissed_at";
const SESSION_KEY = "iw_upgrade_nudge_shown";
const DISMISS_TTL_MS = 24 * 60 * 60 * 1000; // 24h
const SCROLL_THRESHOLD = 0.6; // show after scrolling 60% of doc
const EXIT_INTENT_Y = 8;     // px from top = exit intent
const BLOCKED_PATHS = ["/subscribe", "/payment", "/login", "/signup", "/register", "/portal"];

const isDismissed = () => {
  try {
    const v = localStorage.getItem(DISMISS_KEY);
    if (!v) return false;
    return Date.now() - Number(v) < DISMISS_TTL_MS;
  } catch { return false; }
};

const wasShownThisSession = () => {
  try { return sessionStorage.getItem(SESSION_KEY) === "1"; } catch { return false; }
};

const markShown = () => {
  try { sessionStorage.setItem(SESSION_KEY, "1"); } catch {}
};

export const UpgradeNudge = () => {
  const { user } = useApp();
  const location = useLocation();
  const [visible, setVisible] = useState(false);
  const triggeredRef = useRef(false);

  const isFree = user && (user.access_tier ?? 0) < 2;
  // Founders are exempt from upgrade nudges across the whole app during launch.
  const isFounder = Boolean(user?.founding_member);
  // Guests already receive the GuestCoach tips, stacking this bottom-right
  // nudge on top would double-popup the same spot.
  const isDemo = Boolean(user?.isDemo);
  const isBlockedPath = BLOCKED_PATHS.some(p => location.pathname.startsWith(p));

  useEffect(() => {
    if (!isFree || isFounder || isDemo || isBlockedPath || isDismissed() || wasShownThisSession()) return;

    const show = (reason) => {
      if (triggeredRef.current) return;
      triggeredRef.current = true;
      markShown();
      setVisible(true);
      // Log for analytics (best-effort)
      try { console.log(`[UpgradeNudge] shown via ${reason}`); } catch {}
    };

    const onMouseOut = (e) => {
      // Exit-intent: mouse crossed above the viewport
      if (e.clientY <= EXIT_INTENT_Y && (!e.relatedTarget && !e.toElement)) {
        show("exit-intent");
      }
    };

    const onScroll = () => {
      const doc = document.documentElement;
      const scrolled = (window.scrollY + window.innerHeight) / doc.scrollHeight;
      if (scrolled >= SCROLL_THRESHOLD) {
        show("scroll");
      }
    };

    document.addEventListener("mouseout", onMouseOut);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      document.removeEventListener("mouseout", onMouseOut);
      window.removeEventListener("scroll", onScroll);
    };
  }, [isFree, isFounder, isDemo, isBlockedPath]);

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch {}
    setVisible(false);
  };

  // Free-premium launch window: suppress ALL upgrade prompts everywhere.
  if (isPromptFree()) return null;
  if (!visible || !isFree || isFounder || isDemo || isBlockedPath) return null;

  return (
    <div
      className="fixed bottom-5 right-5 z-[60] max-w-sm animate-slide-in-right"
      role="dialog"
      aria-label="Upgrade prompt"
      data-testid="upgrade-nudge"
      style={{ animation: "nudge-slide-in 0.5s cubic-bezier(0.16, 1, 0.3, 1) both" }}
    >
      <style>{`
        @keyframes nudge-slide-in {
          from { transform: translateX(120%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
      `}</style>
      <div className="rounded-2xl p-4 pr-10 bg-gradient-to-br from-amber-950/95 via-orange-950/90 to-purple-950/90 border-2 border-amber-400/50 shadow-2xl shadow-amber-500/20 backdrop-blur-sm relative">
        <button
          onClick={dismiss}
          className="absolute top-2 right-2 p-1.5 rounded-lg text-white/40 hover:text-white/80 hover:bg-white/5 transition-colors"
          aria-label="Dismiss upgrade prompt"
          data-testid="upgrade-nudge-dismiss"
        >
          <X size={14} />
        </button>
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center shrink-0 shadow-lg shadow-amber-500/30">
            <Crown size={18} className="text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/25 border border-amber-400/40 text-amber-200 text-[9px] font-fredoka font-bold uppercase tracking-wider mb-1.5">
              <Zap size={8} /> {FOUNDER_UPSELL.eyebrow}
            </div>
            <h3 className="text-white font-fredoka font-bold text-sm mb-0.5">{FOUNDER_UPSELL.headline}</h3>
            <p className="text-white/60 text-xs font-nunito leading-relaxed">
              Learning Forest Academy, AI tutor, Family Party Games &amp; more — Founder Plus from <strong className="text-amber-300">{FOUNDER_UPSELL.price}{FOUNDER_UPSELL.period}</strong>. Lock in lifetime founder pricing.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 mt-3">
          <Link
            to="/subscribe"
            onClick={() => { markShown(); setVisible(false); }}
            className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-gradient-to-r from-amber-500 to-orange-500 text-white font-fredoka font-bold text-xs hover:scale-[1.02] transition-transform shadow-lg"
            data-testid="upgrade-nudge-cta"
          >
            <Sparkles size={12} /> {FOUNDER_UPSELL.cta}
          </Link>
          <button
            onClick={dismiss}
            className="px-3 py-2 rounded-lg text-white/50 hover:text-white/80 text-xs font-fredoka font-bold transition-colors"
            data-testid="upgrade-nudge-later"
          >
            Maybe later
          </button>
        </div>
      </div>
    </div>
  );
};

export default UpgradeNudge;
